import {Router} from 'next/dist/client/router';
import {useDispatch} from 'react-redux';
import {pAlert} from '../../common/component/PAlert';
import {REGION} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {showIdVerificationModal} from '../../common/function';
import useIsomorphicLayoutEffect from '../../common/hook/useIsomorphicLayoutEffect';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {PStore} from '../../common/type';
import {ButtonSet} from '../../common/type/alert';
import {processApiError} from '../../common/util/api/apiError';
import {
  getCurrentClientPage,
  getUrlWithFilteredQuery,
  routeTo,
} from '../../common/util/PageRouter';
import {sendAuthEvent, sendAuthViewImp} from '../common';
import {actions} from '../state/action';
import {NextAuthStep} from '../type';

export default function useAuthResult(router: Router) {
  const dispatch = useDispatch();
  const isKorea = REGION === 'kor';
  const [apiParamsAuthResult, apiError] = useTypedSelector(state => [
    state.auth.apiParamsAuthResult,
    state.auth.apiError,
  ]);
  useIsomorphicLayoutEffect(() => {
    if (apiParamsAuthResult || apiError) {
      const isOAuthPage = getCurrentClientPage() === Page.AuthOAuth;
      if (!isOAuthPage) {
        const returnUrl = getUrlWithFilteredQuery(
          router.asPath,
          k => !AUTH_RESULT_QUERY_KEYS.includes(k)
        );
        routeTo(returnUrl, {isReplace: true, isNotCancelable: true});
      }
      if (apiError) {
        processApiError({
          error: {errorType: apiError.type, errorMessage: apiError.msg},
        });
        dispatch(actions.setValue('apiError', undefined));
      } else if (apiParamsAuthResult) {
        const {authStatus, needReload = true} = apiParamsAuthResult;
        if (
          authStatus === NextAuthStep.NeedTermAcceptance ||
          authStatus === NextAuthStep.NeedReactivateAuth
        ) {
          routeTo({page: Page.AuthTerms}, {isReplace: isOAuthPage});
        } else if (authStatus === NextAuthStep.NeedReactivateAuthFinish) {
          pAlert({
            title: t => t('inactive_user_popup_title'),
            desc: t => t('inactive_user_popup_content'),
            isTextAlignCenter: false,
            isForced: true,
          }).then(() => dispatch(actions.fetchFinish(needReload)));
        } else if (authStatus === NextAuthStep.NeedAuthFinish) {
          dispatch(actions.fetchFinish(needReload));
        } else if (authStatus === NextAuthStep.NeedEvictionConfirmation) {
          sendAuthViewImp({
            customProps: {popup_type: 'other_device_log_out'},
            viewImpContents: {name: 'popup'},
          });
          pAlert({
            title: t =>
              t(
                isKorea
                  ? 'login_korea_multiple_device_popup_title'
                  : 'login_another_device_popup'
              ),
            desc: isKorea
              ? t => t('login_korea_multiple_device_popup_content')
              : undefined,
            buttonSet: ButtonSet.CancelAndConfirm,
          }).then(({okClicked, isDimmedClicked}) => {
            if (okClicked) {
              sendAuthEvent({
                actionName: 'popup_other_device_logout',
                customProps: {
                  event_layer: 'popup',
                  popup_type: 'other_device_log_out',
                },
              });
              dispatch(actions.fetchFinish(needReload));
            } else {
              sendAuthEvent({
                actionName: 'popup_close',
                customProps: {
                  event_layer: isDimmedClicked ? 'dimmed' : 'popup',
                  popup_type: 'other_device_log_out',
                },
              });
            }
          });
        } else if (authStatus === NextAuthStep.NeedParentIdVerification) {
          showIdVerificationModal('parent');
        } else if (authStatus === NextAuthStep.NeedUserIdVerification) {
          showIdVerificationModal('under14');
        }
      }
    }
  }, [apiParamsAuthResult, apiError, dispatch, router, isKorea]);
}

export function setAuthResult({store, query}: {store: PStore; query: any}) {
  const {
    session_id,
    signup_status,
    signup_type,
    auth_status,
    error_type,
    error_message,
    verification_session_id,
  } = query;
  if (error_type) {
    store.dispatch(
      actions.setValue('apiError', {type: error_type, msg: error_message})
    );
  } else if (auth_status) {
    const result = {
      sessionId: session_id,
      signupType: signup_type,
      currentStatus: signup_status,
      authStatus: auth_status,
      invalidate: true,
      verificationSessionId: verification_session_id,
    };
    store.dispatch(actions.setValue('apiParamsAuthResult', result));
  }
}
const AUTH_RESULT_QUERY_KEYS = [
  'session_id',
  'status_code',
  'entry_method',
  'signup_status',
  'signup_type',
  'auth_status',
  'error_type',
  'error_message',
  'verification_session_id',
];
